import type { DashboardRefresh, DashboardTimeRange } from "./types";

type RelativeRange = Extract<DashboardTimeRange, { mode: "relative" }>;

/**
 * The periods an author picks from in one list, in plain words. Hourly periods
 * end now; day periods end at today 00:00 UTC, so they cover whole days. Any
 * other relative period is "custom", and the two other modes keep their own
 * entry, so every stored policy maps to exactly one choice.
 */
export const PERIOD_PRESETS: readonly { value: string; label: string; range: RelativeRange }[] = [
  {
    value: "1h",
    label: "Last hour",
    range: { mode: "relative", amount: 1, unit: "hour", endAt: "now" },
  },
  {
    value: "6h",
    label: "Last 6 hours",
    range: { mode: "relative", amount: 6, unit: "hour", endAt: "now" },
  },
  {
    value: "24h",
    label: "Last 24 hours",
    range: { mode: "relative", amount: 24, unit: "hour", endAt: "now" },
  },
  {
    value: "yesterday",
    label: "Yesterday",
    range: { mode: "relative", amount: 1, unit: "day", endAt: "start-of-today" },
  },
  {
    value: "7d",
    label: "Last 7 days",
    range: { mode: "relative", amount: 7, unit: "day", endAt: "start-of-today" },
  },
  {
    value: "30d",
    label: "Last 30 days",
    range: { mode: "relative", amount: 30, unit: "day", endAt: "start-of-today" },
  },
];

export const PERIOD_CHOICES: readonly { value: string; label: string }[] = [
  ...PERIOD_PRESETS.map(({ value, label }) => ({ value, label })),
  { value: "custom", label: "Rolling period…" },
  { value: "scheduled", label: "Scheduled window…" },
  { value: "absolute", label: "Fixed dates…" },
  { value: "event-relative", label: "Selected event" },
];

export function periodChoice(range: DashboardTimeRange): string {
  if (range.mode !== "relative") return range.mode;
  return (
    PERIOD_PRESETS.find(
      (preset) =>
        preset.range.amount === range.amount &&
        preset.range.unit === range.unit &&
        preset.range.endAt === range.endAt,
    )?.value ?? "custom"
  );
}

/** How often the data is fetched again, from every minute to once a day. */
export const REFRESH_PRESETS: readonly { seconds: number; label: string }[] = [
  { seconds: 60, label: "Every minute" },
  { seconds: 300, label: "Every 5 minutes" },
  { seconds: 900, label: "Every 15 minutes" },
  { seconds: 3600, label: "Every hour" },
  { seconds: 21600, label: "Every 6 hours" },
  { seconds: 86400, label: "Once a day" },
];

export function refreshChoice(refresh: DashboardRefresh): string {
  if (getRefreshSchedule(refresh) === "daily-utc") return "86400";
  if (refresh.intervalSeconds === 86400) return "custom";
  return REFRESH_PRESETS.some((preset) => preset.seconds === refresh.intervalSeconds)
    ? String(refresh.intervalSeconds)
    : "custom";
}

export function getRefreshSchedule(refresh: DashboardRefresh): "interval" | "daily-utc" {
  return refresh.schedule ?? (refresh.intervalSeconds === 86400 ? "daily-utc" : "interval");
}

/** "Every 2 hours", "Every 90 minutes", "Once a day"… for any interval. */
export function refreshLabel(
  seconds: number,
  schedule?: DashboardRefresh["schedule"],
  time = "00:00",
): string {
  if (getRefreshSchedule({ intervalSeconds: seconds, onFocus: "never", schedule }) === "daily-utc")
    return time === "00:00" ? "Once a day" : `Once a day at ${time} UTC`;
  const preset = REFRESH_PRESETS.find(
    (entry) => entry.seconds === seconds && entry.seconds !== 86400,
  );
  if (preset) return preset.label;
  const minutes = Math.round(seconds / 60);
  if (minutes % 1440 === 0) return minutes === 1440 ? "Every day" : `Every ${minutes / 1440} days`;
  if (minutes % 60 === 0) return `Every ${minutes / 60} hours`;
  return `Every ${minutes} minutes`;
}

/** The largest whole unit for an interval, so a custom value reads "2 hours", not "120 minutes". */
export function refreshParts(seconds: number): { amount: number; unit: "minute" | "hour" | "day" } {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes % 1440 === 0) return { amount: minutes / 1440, unit: "day" };
  if (minutes % 60 === 0) return { amount: minutes / 60, unit: "hour" };
  return { amount: minutes, unit: "minute" };
}

export const UNIT_SECONDS = { minute: 60, hour: 3600, day: 86400 } as const;
